/**
 * 문서기안 팝업 (그룹웨어).
 *
 * 양식조회(SN_GRW_BAS_REG_003)에서 양식을 고른 뒤 "기안작성"으로 띄운다.
 * 왼쪽은 본문 편집기, 오른쪽은 결재라인 · 수신 · 첨부 영역이다.
 *
 * 이 브랜치에는 양식 본문 편집기가 없어서 팝업 안에 자체적으로 넣었다.
 * 다른 브랜치의 공용 편집기와 이름이 겹치지 않도록 파일 안에서만 쓰는
 * 이름(GrwDraftBody)으로 두고 export 하지 않는다.
 *
 * 저장 · 상신 · 결재라인/수신 선택은 화면에서 넘긴 콜백으로 위임한다 —
 * 이 팝업은 API를 부르지 않는다.
 */
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, TextField } from "@mui/material";
import dayjs from "dayjs";
import { global } from "@shared/libs/Global";
import { showPopupModal } from "@widgets/CommonModal";
import GrwApprovalLinePopup, {
  type GrwDeptRow,
  type GrwEmpRow,
  type GrwLineRow,
  type GrwPathGroup,
} from "@widgets/commonpopup/GrwApprovalLinePopup";
import SnTab from "@shared/components/SnTab";
import { StyleSettings } from "../../styles/StyleSettings";

//#region 타입

/** 결재라인 한 줄 */
export interface GrwApprovalRow {
  cd_dept?: string;
  nm_dept: string;
  cd_emp?: string;
  nm_emp: string;
  /** 역할 — 결재 / 합의 / 공람 / 수신처 / 시행자 */
  nm_role: string;
  /** 직급 코드 (mas_emp.fg_pos — 공통코드 HRS007) */
  fg_pos?: string;
  /** 직급명 (HRS007 조인 결과) */
  nm_pos?: string;
}

/**
 * grw_approvel 한 줄 — DB 컬럼명 그대로 담는다.
 *
 * cd_firm·no_approve는 화면이 헤더와 같은 값으로 채워 넣으므로 여기엔 없다.
 * 전부 NOT NULL이라 빈 값을 만들지 않는다.
 */
export interface GrwApprovalLineRow {
  /** PK. 1부터 순번 */
  no_line: number;
  /** 결재 처리 순서 */
  no_seq: number;
  /** 결재/합의 구분 */
  fg_approve: string;
  cd_emp: string;
  cd_dept: string;
  /** 라인 상태 — 대기 / 처리완료 */
  st_line: string;
  /** 지금 처리할 차례인지 */
  yn_current: string;
  /** 필수 결재인지 */
  yn_required: string;
}

/** 수신 한 줄 */
export interface GrwReceiverRow {
  cd_dept?: string;
  nm_dept: string;
  cd_emp?: string;
  nm_emp: string;
}

/** 결재상태 — 기안(임시저장) 01, 상신 02 */
export const ST_APPROVE = { DRAFT: "01", SUBMIT: "02" } as const;

/** 팝업이 들고 있는 기안 내용 */
export interface GrwDraftData {
  /** 채번한 문서번호 */
  no_doc: string;
  /** 결재상태 — 임시저장이면 01, 상신이면 02로 담겨 나간다 */
  st_approve: string;
  /** 어느 양식으로 기안했는지 */
  no_form: string;
  nm_form: string;
  /** 기안 문서 제목 */
  nm_title: string;
  /** 기안자 */
  nm_emp: string;
  /** 작성일 (YYYYMMDD) */
  dt_write: string;
  /** 본문 HTML */
  js_body: string;
  /** 결재경로 팝업에서 고른 사람 그대로 (기안자 없음) */
  approvalRows: GrwApprovalRow[];
  /** grw_approvel에 그대로 넣을 결재라인 — 기안자가 1번이고 순번이 매겨져 있다 */
  approvalLines: GrwApprovalLineRow[];
  receiverRows: GrwReceiverRow[];
  files: File[];
}

//#endregion

//#region 본문 변수 치환

/**
 * 양식에 등록된 변수 목록을 배열로 되돌린다.
 * 같은 값이 경로에 따라 배열 / JSON 문자열 / {type:'jsonb',value} / 이중 인코딩으로 온다.
 */
const parseVarRows = (raw: any): any[] => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "object") {
    const inner = (raw as any).value;
    return typeof inner === "string" ? parseVarRows(inner) : [];
  }
  if (typeof raw !== "string") return [];

  let value: any = raw;
  for (let i = 0; i < 3; i++) {
    try {
      value = JSON.parse(typeof value === "string" ? value.trim() : value);
    } catch {
      return [];
    }
    if (Array.isArray(value)) return value;
    if (typeof value !== "string") return [];
  }
  return [];
};

/** Default에 넣으면 열 때마다 계산되는 예약어 */
const DYNAMIC_DEFAULTS: Record<string, () => string> = (() => {
  const user = () => global.getState().userInfo ?? {};
  const groups: { keys: string[]; get: () => string }[] = [
    { keys: ["부서명", "부서"], get: () => user().nm_dept ?? "" },
    { keys: ["이름", "성명", "사원명"], get: () => user().nm_emp ?? "" },
    { keys: ["직위", "직책"], get: () => user().nm_fg_role ?? "" },
    {
      keys: ["오늘날짜", "오늘", "금일", "today", "TODAY"],
      get: () => dayjs().format("YYYYMMDD"),
    },
  ];
  return Object.fromEntries(
    groups.flatMap((g) => g.keys.map((k) => [k, g.get]))
  );
})();

/**
 * 문서번호를 가리키는 예약어들.
 * 양식마다 변수명을 다르게 등록해둬서 흔한 표기를 모두 받아준다.
 */
const DOC_NO_KEYS = ["문서번호", "문서No", "기안번호", "no_doc"];

/**
 * 문서정보 칸의 요소 id — 레거시 양식이 쓰던 표기를 그대로 따른다.
 *   <TH>문 서 번 호</TH><TD>&nbsp;<SPAN ID='NO_DOC'> 개발본부-202608040011</SPAN></TD>
 * 같은 표에 DT_APP(신청일자) · CD_DEPT(부서명) · CD_EMP_WT(신청자) · NM_DOC(제목)도 있다.
 */
const DOC_NO_ELEMENT_ID = "NO_DOC";

/**
 * 변수 한 건이 본문에 꽂힐 값. Format이 있고 날짜로 읽히면 그 표기로 바꾼다.
 * runtime은 기안 시점에야 정해지는 예약어(문서번호 등) — 고정 예약어보다 우선한다.
 */
const resolveValue = (v: any, runtime: Record<string, string> = {}): string => {
  const raw = (v?.default ?? "").toString().trim();
  const fmt = (v?.format ?? "").toString().trim();
  if (!raw) return "";

  const value = runtime[raw] ?? DYNAMIC_DEFAULTS[raw]?.() ?? raw;
  if (!value || !fmt) return value;

  const d = dayjs(value);
  return d.isValid() ? d.format(fmt) : value;
};

/** ID에 정규식 메타문자가 섞여도 글자 그대로 찾도록 이스케이프 */
const escapeForRegExp = (id: string) =>
  id.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * 양식 본문에서 id로 자리를 잡아둔 칸의 내용을 바꾼다.
 *
 * 결재란(@11rname 등)은 결재 진행에 따라 값이 바뀌므로 본문에 박지 않고 그릴 때마다 stamps로
 * 덧씌우지만, 문서번호는 기안 시점에 확정돼 문서와 함께 저장돼야 하는 값이라 본문을 직접 고친다.
 *
 * DOM으로 파싱하지 않고 문자열을 바꾼다. 이 양식들은 MSHTML 시절 HTML이라 파서를 태워
 * 다시 뽑으면 태그·따옴표 표기가 통째로 정규화돼 레이아웃이 틀어질 수 있다.
 * id는 따옴표가 있을 수도(ID='NO_DOC') 없을 수도 있고 대소문자도 제각각이라 둘 다 받는다.
 */
const setElementText = (html: string, id: string, text: string): string => {
  const escaped = escapeForRegExp(id);
  const pattern = new RegExp(
    `(<(\\w+)[^>]*\\sid\\s*=\\s*['"]?${escaped}['"]?(?!['"\\w-])[^>]*>)([\\s\\S]*?)(<\\/\\2\\s*>)`,
    "i"
  );
  return html.replace(pattern, (_m, open, _tag, _inner, close) =>
    [open, text, close].join("")
  );
};

/**
 * JSON 문자열 스칼라로 감싸인 원문을 벗겨낸다.
 *
 * HTML을 jsonb 컬럼에 넣으면 JSON 스칼라("...\n...")로 저장돼, 읽을 때 따옴표로 감싸이고
 * 개행/따옴표가 \n, \" 로 이스케이프된 상태로 내려온다. 그대로 그리면 \n이 글자로 찍힌다.
 * HTML 원문은 절대 "로 시작하지 않으므로 앞뒤가 "인 경우만 푼다(잘못 벗길 위험 없음).
 */
const unwrapJsonString = (text: string): string => {
  let out = text;
  for (let i = 0; i < 3; i++) {
    const trimmed = out.trim();
    if (!trimmed.startsWith('"') || !trimmed.endsWith('"')) break;
    try {
      const parsed = JSON.parse(trimmed);
      if (typeof parsed !== "string") break;
      out = parsed;
    } catch {
      break;
    }
  }
  return out;
};

/**
 * 본문 컬럼을 원문 문자열 하나로 만든다.
 *
 * 드라이버/매퍼에 따라 raw 컬럼이 문자열이 아니라 {type:'jsonb', value:'...'} 로
 * 감싸이거나 배열로 내려온다. 그대로 쓰면 문자열 메서드에서 터진다.
 * JSON.parse로 통째로 해석하지는 않는다 — HTML 원문을 JSON으로 보면 안 되기 때문.
 */
const toRawText = (value: any): string => {
  if (value == null) return "";
  if (typeof value === "string") return unwrapJsonString(value);
  if (Array.isArray(value)) return value.map(toRawText).join("");
  if (typeof value === "object" && typeof (value as any).value === "string") {
    return toRawText((value as any).value);
  }
  return "";
};

/**
 * 본문의 @%ID@% 토큰을 변수 값으로 바꾼다.
 * 양식 원문은 토큰을 유지해야 하므로 기안 시작 시점에 한 번만 통과시킨다.
 *
 * 등록된 변수를 먼저 채우고, 그 다음 runtime 예약어(문서번호 등)를 이름 그대로 한 번 더 훑는다.
 * 양식에 변수로 등록하지 않고 @%문서번호@% 토큰만 박아둔 경우도 채워지게 하기 위해서다.
 */
const applyVars = (
  html: any,
  vars: any[],
  runtime: Record<string, string> = {}
): string => {
  const replaced = vars.reduce((acc: string, v) => {
    const id = (v?.id ?? "").toString().trim();
    if (!id) return acc;
    return acc.replace(
      new RegExp(`@%${escapeForRegExp(id)}@%`, "g"),
      resolveValue(v, runtime)
    );
  }, toRawText(html));

  return Object.entries(runtime).reduce(
    (acc, [key, value]) =>
      acc.replace(new RegExp(`@%${escapeForRegExp(key)}@%`, "g"), value),
    replaced
  );
};

/**
 * 양식 본문을 기안 시작 상태로 만든다.
 *   1) @%변수@% 토큰 치환 (문서번호도 예약어로 받는다)
 *   2) id로 자리를 잡아둔 문서번호 칸(SPAN ID='NO_DOC') 채우기
 *
 * 화면도 팝업을 열기 전에 같은 본문이 필요해서(기안작성 시 grw_approveh INSERT)
 * 내보낸다. 팝업과 화면이 서로 다른 본문을 쓰지 않도록 여기 한 곳만 고치면 된다.
 */
export const buildDraftBody = (form: any, noDoc: string): string => {
  const html = applyVars(
    form?.js_body,
    parseVarRows(form?.js_param),
    Object.fromEntries(DOC_NO_KEYS.map((key) => [key, noDoc]))
  );
  return noDoc ? setElementText(html, DOC_NO_ELEMENT_ID, noDoc) : html;
};

/** 기안 제목 초기값 — 고른 양식명으로 시작한다 */
export const buildDraftTitle = (form: any): string => form?.nm_form ?? "";

//#endregion

//#region 본문 편집기 (이 팝업 전용)

/** 편집 편의로 끼워넣는 요소 표시 — 값을 올릴 때 걷어낸다 */
const EDITOR_ONLY = "data-grw-editor-only";
/** contenteditable을 손댄 요소의 원래 값 보관 자리 */
const CE_ORIG = "data-grw-ce-orig";

/**
 * 결재란에 찍어 넣은 이름·직급 표시.
 *
 * EDITOR_ONLY를 달지 않으므로 저장(buildHtml) 때 함께 나간다 — 임시저장·상신한 본문은
 * 화면에서 본 그대로(결재자가 찍힌 상태) 보관된다.
 * 이 속성으로 다시 찾아 지울 수 있어서, 결재라인이 바뀌면 통째로 걷어내고 다시 찍는다.
 */
const STAMP_ATTR = "data-grw-stamp";

/**
 * 결재란 셀 id 규칙 — 레거시 양식이 쓰던 표기를 그대로 따른다.
 *   @ + [블록] + [순번] + r + [role|sign|name]
 *      1=결재 2=합의    1~6      직급/서명/이름
 */
const stampCellId = (block: number, seq: number, kind: string) =>
  `@${block}${seq}r${kind}`;

/** 본문 결재란에 칸이 있는 역할만 채운다 (공람·수신처·시행자는 칸이 없다) */
const BLOCK_OF_ROLE: Record<string, number> = { 결재: 1, 합의: 2 };

/**
 * grw_approvel.fg_approve — 결재구분. 공통코드 GRW055.
 * 결재경로 팝업의 탭 이름을 그대로 키로 쓴다.
 */
const FG_APPROVE: Record<string, string> = {
  결재: "APR",
  합의: "AGR",
  수신처: "RCV",
};

/** grw_approvel.st_line — 결재라인상태. 공통코드 GRW054 */
const ST_LINE = {
  /** 예정 */
  WAIT: "WAIT",
  /** 현재 처리 대상 */
  TODO: "TODO",
  /** 승인·합의완료 */
  DONE: "DONE",
  /** 반려 */
  REJECT: "REJECT",
  /** 회수·취소 */
  CANCEL: "CANCEL",
} as const;

/** 이 탭에 담긴 사람은 결재라인이 아니라 수신 표로 간다 */
const RECEIVER_ROLE = "수신처";

/** 한 블록에 들어가는 칸 수 */
const SLOTS_PER_BLOCK = 6;

const escapeHtml = (s: string) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/**
 * 결재 도장 — 서명칸(@n1rsign)에 찍는다.
 *
 * 저장된 본문에 그대로 남아 나중에 다른 곳에서 열릴 수 있으므로 외부 CSS·이미지 없이
 * 인라인 스타일만 쓴다. 색·크기를 바꾸려면 여기만 고치면 된다.
 */
const signStampHtml = (nmEmp: string, dt: Date): string => {
  const day = dayjs(dt).format("MM.DD");
  return (
    `<span style="display:inline-block;text-align:center;line-height:1;vertical-align:middle">` +
    `<span style="display:inline-block;width:44px;height:44px;box-sizing:border-box;` +
    `border:2px solid #d40000;border-radius:50%;` +
    `box-shadow:inset 0 0 0 2px #fff,inset 0 0 0 3px #d40000;` +
    `color:#d40000;font-family:'굴림';font-size:9pt;line-height:40px;overflow:hidden;">` +
    `${escapeHtml(nmEmp)}</span>` +
    `<br><span style="font-family:'굴림';font-size:7pt;color:#333">${day}</span>` +
    `</span>`
  );
};

/**
 * 편집 중에만 적용하는 보정 CSS.
 * - min-height: 본문이 짧으면 첫 줄 말고는 눌러도 커서가 안 잡힌다.
 * - user-select: 인쇄용 양식이 선택을 막아둔 구간이 있으면 캐럿을 못 놓는다.
 */
const EDITOR_CSS = [
  "html,body{min-height:100%;box-sizing:border-box;}",
  "*{user-select:text !important;-webkit-user-select:text !important;}",
].join("");

/**
 * 문서의 DOCTYPE을 원문 그대로 되살린다.
 *
 * 결재양식은 MSHTML 시절에 만들어져 DOCTYPE이 없는 것이 많고, 그런 문서는 브라우저가
 * "쿼크 모드"로 그린다. 쿼크 모드에서는 표의 height 속성·bordercolor·테두리 계산이
 * 표준 모드와 다르게 동작해서, 없던 <!DOCTYPE html>을 붙이면 결재란 같은 레이아웃이 틀어진다.
 * 그래서 있으면 그대로, 없으면 안 붙인다.
 */
const serializeDoctype = (doc: Document): string => {
  const dt = doc.doctype;
  if (!dt) return "";
  const pub = dt.publicId ? ` PUBLIC "${dt.publicId}"` : "";
  const sys = dt.systemId
    ? `${dt.publicId ? "" : " SYSTEM"} "${dt.systemId}"`
    : "";
  return `<!DOCTYPE ${dt.name}${pub}${sys}>\n`;
};

const stripScripts = (html: any) =>
  typeof html === "string"
    ? html.replace(/<script[\s\S]*?<\/script>/gi, "")
    : "";

const FONT_FAMILIES = ["돋움", "굴림", "바탕", "맑은 고딕", "Arial"];

interface BodyProps {
  body: string;
  setBody: (v: string) => void;
  /** 결재란에 채워 넣을 값 (셀 id → 보여줄 글자) */
  stamps?: Record<string, string>;
  /** 서명칸에 찍을 도장 (셀 id → HTML). 글자가 아니라 마크업이라 따로 받는다 */
  signStamps?: Record<string, string>;
}

/**
 * 양식 HTML 편집기.
 *
 * 결재양식은 전체 문서형 HTML(<head><style>, 레이아웃 <table>)이라 위지윅 라이브러리에
 * 넣으면 구조가 벗겨진다. 브라우저가 원문을 그대로 렌더하는 iframe을 designMode로 만들고
 * execCommand 툴바를 얹는다.
 */
/** 편집기 바깥에서 지금 내용을 꺼내갈 때 쓴다 */
interface BodyHandle {
  /**
   * 지금 iframe 내용을 저장용 HTML로 만든다.
   *
   * body state는 blur·툴바 조작 때만 갱신되므로, 편집기를 건드리지 않고 결재라인만
   * 추가한 뒤 바로 저장하면 state가 낡아 있다. 저장 직전에는 이걸로 직접 읽어간다.
   */
  getHtml: () => string | null;
  /**
   * 결재란·서명칸을 지금 즉시 다시 찍는다.
   *
   * props로 넘기면 다음 렌더에야 반영되는데, 상신은 도장을 찍은 본문을 그 자리에서
   * 읽어가야 하므로 렌더를 기다릴 수 없다.
   */
  applyStamps: (
    nextStamps?: Record<string, string>,
    nextSigns?: Record<string, string>
  ) => void;
}

const GrwDraftBody = forwardRef<BodyHandle, BodyProps>(function GrwDraftBody(
  { body, setBody, stamps, signStamps },
  ref
) {
  const [mode, setMode] = useState<"edit" | "html">("edit");
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const loadedRef = useRef<string | null>(null);
  const lastEmittedRef = useRef<string | null>(null);
  const baselineRef = useRef<string | null>(null);
  const savedRangeRef = useRef<Range | null>(null);

  const getDoc = () => iframeRef.current?.contentDocument || null;
  const getWin = () => iframeRef.current?.contentWindow || null;

  /** 지금 iframe 내용을 저장용 HTML로 만든다 (편집용으로 끼운 것은 걷어냄) */
  const buildHtml = (): string | null => {
    const doc = getDoc();
    if (!doc?.documentElement) return null;

    const clone = doc.documentElement.cloneNode(true) as HTMLElement;
    clone.querySelectorAll(`[${EDITOR_ONLY}]`).forEach((el) => el.remove());
    clone.querySelectorAll(`[${CE_ORIG}]`).forEach((el) => {
      const orig = el.getAttribute(CE_ORIG) ?? "";
      el.removeAttribute(CE_ORIG);
      if (orig) el.setAttribute("contenteditable", orig);
      else el.removeAttribute("contenteditable");
    });
    return serializeDoctype(doc) + clone.outerHTML;
  };

  // 저장 직전에 팝업이 현재 본문을 직접 읽어가고, 도장도 그 자리에서 찍는다
  useImperativeHandle(ref, () => ({
    getHtml: buildHtml,
    applyStamps: (nextStamps, nextSigns) => applyStamps(nextStamps, nextSigns),
  }));

  /**
   * 부모로 올린다. 브라우저가 문서를 실을 때 원문을 정규화하므로,
   * 원문과 비교하면 클릭만 해도 "바뀜"이 된다. 정규화가 끝난 기준선과 비교한다.
   */
  const emit = () => {
    const html = buildHtml();
    if (html == null || html === baselineRef.current) return;
    baselineRef.current = html;
    lastEmittedRef.current = html;
    setBody(html);
  };
  const emitRef = useRef(emit);
  emitRef.current = emit;

  const saveSelection = () => {
    const sel = getWin()?.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedRangeRef.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const exec = (command: string, value?: string) => {
    const doc = getDoc();
    const win = getWin();
    if (!doc) return;
    win?.focus();
    const sel = win?.getSelection();
    if (sel && savedRangeRef.current) {
      sel.removeAllRanges();
      sel.addRange(savedRangeRef.current);
    }
    try {
      doc.execCommand(command, false, value);
    } catch {
      /* 미지원 명령 무시 */
    }
    saveSelection();
    emit();
  };

  /**
   * 편집 가능 상태로 만든다.
   * designMode가 조용히 안 걸리는 경우가 있어 body에 contenteditable을 함께 건다.
   * 원문이 contenteditable="false"로 잠가둔 구간도 편집 중에는 풀어준다.
   */
  const enableEditing = () => {
    const doc = getDoc();
    if (!doc) return;
    try {
      doc.designMode = "on";
    } catch {
      /* noop */
    }
    if (doc.body && !doc.body.isContentEditable) {
      if (!doc.body.hasAttribute(CE_ORIG)) {
        doc.body.setAttribute(
          CE_ORIG,
          doc.body.getAttribute("contenteditable") ?? ""
        );
      }
      doc.body.setAttribute("contenteditable", "true");
    }
    doc.querySelectorAll("[contenteditable]").forEach((el) => {
      if ((el.getAttribute("contenteditable") ?? "").toLowerCase() === "true") {
        return;
      }
      if (!el.hasAttribute(CE_ORIG)) {
        el.setAttribute(CE_ORIG, el.getAttribute("contenteditable") ?? "");
      }
      el.setAttribute("contenteditable", "true");
    });
    try {
      doc.execCommand("styleWithCSS", false, "true");
    } catch {
      /* noop */
    }
  };

  const loadDoc = (html: string) => {
    const doc = getDoc();
    if (!doc) return;
    loadedRef.current = html;
    doc.open();
    doc.write(stripScripts(html) || "<html><body></body></html>");
    doc.close();

    const style = doc.createElement("style");
    style.setAttribute(EDITOR_ONLY, "1");
    style.textContent = EDITOR_CSS;
    (doc.head || doc.documentElement)?.appendChild(style);

    enableEditing();
    requestAnimationFrame(enableEditing);
    baselineRef.current = buildHtml();

    // 기준선을 잡은 뒤에 찍어야 "수정됨"으로 잡히지 않는다
    applyStamps();

    doc.addEventListener("blur", () => emitRef.current(), true);
    doc.addEventListener("selectionchange", saveSelection);
    doc.addEventListener("mouseup", saveSelection);
    doc.addEventListener("keyup", saveSelection);
  };

  /**
   * 결재란 셀에 이름·직급을, 서명칸에 도장을 찍는다.
   *
   * 셀 내용을 갈아치우지 않고 표시용 span을 덧붙인다. EDITOR_ONLY를 달지 않으므로
   * 저장할 때(buildHtml) 같이 나간다 — 화면에서 본 결재란 그대로 보관된다.
   * 결재라인이 바뀌면 STAMP_ATTR로 찾아 걷어내고 다시 찍으므로 중복되지 않는다.
   *
   * 인자를 주면 그 값으로, 안 주면 지금 props로 찍는다. 상신처럼 렌더를 기다릴 수 없을 때
   * 팝업이 값을 직접 넘겨 부른다.
   */
  const applyStamps = (
    nextStamps: Record<string, string> = stamps ?? {},
    nextSigns: Record<string, string> = signStamps ?? {}
  ) => {
    const doc = getDoc();
    if (!doc) return;

    // 이전에 찍은 것부터 걷어낸다
    doc.querySelectorAll(`[${STAMP_ATTR}]`).forEach((el) => el.remove());

    const stampInto = (cellId: string, fill: (el: HTMLElement) => void) => {
      // id에 @가 들어가 CSS 선택자로는 못 찾는다. getElementById를 쓴다.
      const cell = doc.getElementById(cellId);
      if (!cell) return;
      const span = doc.createElement("span");
      span.setAttribute(STAMP_ATTR, "1");
      fill(span);
      cell.appendChild(span);
    };

    Object.entries(nextStamps).forEach(([cellId, text]) => {
      if (!text) return;
      stampInto(cellId, (el) => {
        el.textContent = text;
      });
    });

    Object.entries(nextSigns).forEach(([cellId, html]) => {
      if (!html) return;
      stampInto(cellId, (el) => {
        el.innerHTML = html;
      });
    });
  };

  useEffect(() => {
    if (body === lastEmittedRef.current) return;
    if (body === loadedRef.current) return;
    const id = requestAnimationFrame(() => loadDoc(body));
    return () => cancelAnimationFrame(id);
  }, [body]);

  // 결재라인이나 도장이 바뀌면 다시 찍는다
  useEffect(() => {
    applyStamps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stamps, signStamps]);

  const TbBtn = ({
    cmd,
    value,
    title,
    children,
  }: {
    cmd: string;
    value?: string;
    title: string;
    children: React.ReactNode;
  }) => (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => exec(cmd, value)}
      style={TB_BTN}
    >
      {children}
    </button>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <div style={{ display: "flex", gap: 2, padding: "4px 6px 0", flex: "0 0 auto" }}>
        <button
          type="button"
          onClick={() => {
            if (body !== loadedRef.current) loadDoc(body);
            else requestAnimationFrame(enableEditing);
            setMode("edit");
          }}
          style={TAB_BTN(mode === "edit")}
        >
          에디터
        </button>
        <button
          type="button"
          onClick={() => {
            emit();
            setMode("html");
          }}
          style={TAB_BTN(mode === "html")}
        >
          HTML
        </button>
      </div>

      {mode === "edit" && (
        <div style={TOOLBAR}>
          <select
            title="글꼴"
            defaultValue=""
            onMouseDown={saveSelection}
            onChange={(e) => {
              exec("fontName", e.target.value);
              e.target.value = "";
            }}
            style={TB_SELECT}
          >
            <option value="" disabled>
              글꼴
            </option>
            {FONT_FAMILIES.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
          <select
            title="글자 크기"
            defaultValue=""
            onMouseDown={saveSelection}
            onChange={(e) => {
              exec("fontSize", e.target.value);
              e.target.value = "";
            }}
            style={TB_SELECT}
          >
            <option value="" disabled>
              크기
            </option>
            {[1, 2, 3, 4, 5, 6, 7].map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>

          <span style={SEP} />
          <TbBtn cmd="bold" title="굵게">
            <b>B</b>
          </TbBtn>
          <TbBtn cmd="italic" title="기울임">
            <i>I</i>
          </TbBtn>
          <TbBtn cmd="underline" title="밑줄">
            <u>U</u>
          </TbBtn>
          <TbBtn cmd="strikeThrough" title="취소선">
            <s>S</s>
          </TbBtn>

          <label title="글자색" style={TB_BTN} onMouseDown={saveSelection}>
            <span style={{ fontWeight: 700, color: "#d00" }}>A</span>
            <input
              type="color"
              onChange={(e) => exec("foreColor", e.target.value)}
              style={COLOR_INPUT}
            />
          </label>
          <label title="배경색" style={TB_BTN} onMouseDown={saveSelection}>
            <span style={{ fontWeight: 700, background: "#ff0", padding: "0 2px" }}>
              A
            </span>
            <input
              type="color"
              onChange={(e) => exec("hiliteColor", e.target.value)}
              style={COLOR_INPUT}
            />
          </label>

          <span style={SEP} />
          <TbBtn cmd="justifyLeft" title="왼쪽 정렬">
            ≡
          </TbBtn>
          <TbBtn cmd="justifyCenter" title="가운데 정렬">
            ≡
          </TbBtn>
          <TbBtn cmd="justifyRight" title="오른쪽 정렬">
            ≡
          </TbBtn>
          <TbBtn cmd="insertUnorderedList" title="글머리 기호">
            •
          </TbBtn>
          <TbBtn cmd="insertOrderedList" title="번호 매기기">
            1.
          </TbBtn>
          <TbBtn cmd="outdent" title="내어쓰기">
            ⇤
          </TbBtn>
          <TbBtn cmd="indent" title="들여쓰기">
            ⇥
          </TbBtn>

          <span style={SEP} />
          <button
            type="button"
            title="이미지 삽입(URL)"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              const url = window.prompt("이미지 URL을 입력하세요");
              if (url) exec("insertImage", url);
            }}
            style={TB_BTN}
          >
            🖼
          </button>
          <TbBtn cmd="removeFormat" title="서식 지우기">
            ⌫
          </TbBtn>
          <span style={SEP} />
          <TbBtn cmd="undo" title="실행 취소">
            ↶
          </TbBtn>
          <TbBtn cmd="redo" title="다시 실행">
            ↷
          </TbBtn>
        </div>
      )}

      <div style={{ flex: "1 1 auto", minHeight: 0, position: "relative" }}>
        <iframe
          ref={iframeRef}
          title="grw-draft-body"
          style={{
            width: "100%",
            height: "100%",
            border: "none",
            background: "#fff",
            display: mode === "edit" ? "block" : "none",
          }}
        />
        {mode === "html" && (
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            spellCheck={false}
            style={HTML_AREA}
          />
        )}
      </div>
    </div>
  );
});

//#endregion

/** 표에 항상 이 줄 수만큼은 칸을 그려 빈 표도 모양이 잡히게 한다 */
const MIN_ROWS = 4;

interface Props {
  /** CommonModal이 주입 — 닫기 버튼에서 쓴다 */
  modalRef?: any;
  /** 선택한 양식 행 (양식조회 그리드의 행 데이터) */
  form?: any;
  /**
   * 채번한 문서번호. 화면에서 채번해 넘긴다 (이 팝업은 API를 부르지 않는다).
   * 본문의 문서번호 변수·토큰에 꽂히고 저장값(no_doc)에도 담긴다.
   */
  noDoc?: string;
  /** @deprecated "결재라인"은 표 제목이라 누를 수 없다. 지금은 호출되지 않는다 */
  onApprovalLine?: (data: GrwDraftData) => void;
  /**
   * 결재라인추가 — 넘기지 않으면 기본 결재경로 팝업을 띄운다.
   * 화면에서 직접 다른 걸 띄우고 싶을 때만 주면 된다.
   */
  onApprovalLineAdd?: (data: GrwDraftData) => void;
  /** 결재경로 팝업 왼쪽 부서 목록 */
  depts?: GrwDeptRow[];
  /** 결재경로 팝업 경로그룹 콤보 (onLoadPathGroups를 안 넘길 때의 고정 목록) */
  pathGroups?: GrwPathGroup[];
  /** 결재경로 팝업 경로그룹 조회 · 추가 · 삭제 — 그대로 넘겨준다 */
  onLoadPathGroups?: () => Promise<GrwPathGroup[]> | GrwPathGroup[];
  onAddPathGroup?: (
    nm_group: string,
    lines: GrwLineRow[]
  ) => Promise<boolean | void> | boolean | void;
  onDeletePathGroup?: (
    cd_group: string
  ) => Promise<boolean | void> | boolean | void;
  /** 결재경로 팝업에 표시할 탭 목록. 넘기지 않으면 5개 전부 나온다 */
  approvalLineTabs?: string[];
  /** 결재경로 팝업의 사원 조회 */
  onSearchEmp?: (cond: {
    cd_dept?: string;
    nm_emp?: string;
  }) => Promise<GrwEmpRow[]> | GrwEmpRow[];
  /** @deprecated "수신"은 표 제목이라 누를 수 없다. 지금은 호출되지 않는다 */
  onReceiver?: (data: GrwDraftData) => void;
  /**
   * @deprecated "첨부"는 탭 제목이라 누를 수 없다. 지금은 호출되지 않는다.
   * 파일 선택은 Drag&Drop 영역을 클릭하면 열린다.
   */
  onAttach?: (data: GrwDraftData) => void;
  onTempSave?: (data: GrwDraftData) => void;
  /**
   * 상신. 저장에 성공하면 true를 돌려준다 — 그때만 이 팝업이 닫힌다.
   * 아무것도 돌려주지 않으면 저장 결과를 알 수 없으므로 열어 둔다.
   */
  onSubmit?: (data: GrwDraftData) => Promise<boolean | void> | boolean | void;
}

const GrwDraftPopup = forwardRef((props: Props, ref) => {
  const {
    modalRef,
    form,
    noDoc = "",
    onApprovalLineAdd,
    onTempSave,
    onSubmit,
    depts,
    pathGroups,
    onLoadPathGroups,
    onAddPathGroup,
    onDeletePathGroup,
    approvalLineTabs,
    onSearchEmp,
  } = props;

  const userInfo = global.getState().userInfo;

  /** 양식 본문의 기안 시작 상태. 양식·문서번호가 바뀔 때만 다시 만든다 */
  const initialBody = useMemo(
    () => buildDraftBody(form, noDoc),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form?.js_body, form?.js_param, noDoc]
  );

  // 제목은 고른 양식명으로 시작한다 (대부분 그대로 쓰고, 필요하면 고쳐 쓴다).
  // 팝업은 열 때마다 새로 그려지므로 초기값으로 충분하다.
  const [title, setTitle] = useState(buildDraftTitle(form));
  const [body, setBody] = useState(initialBody);
  const [approvalRows, setApprovalRows] = useState<GrwApprovalRow[]>([]);
  const [receiverRows, setReceiverRows] = useState<GrwReceiverRow[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [isDragOver, setDragOver] = useState(false);

  /** 저장 직전에 편집기에서 지금 본문을 직접 읽어오기 위한 통로 */
  const bodyRef = useRef<BodyHandle>(null);

  /** 상신 처리 중 — 두 번 눌러 결재라인이 두 번 들어가는 것을 막는다 */
  const [submitting, setSubmitting] = useState(false);

  // 기안작성으로 열린 문서는 상신 전까지 01(기안)이다. 상신을 누르면 02로 올라간다.
  const [stApprove, setStApprove] = useState<string>(ST_APPROVE.DRAFT);

  const nmForm = form?.nm_form ?? "";
  const nmEmp = userInfo?.nm_emp ?? "";

  /**
   * 결재라인을 본문 결재란 셀에 매핑한다.
   *
   * 결재는 1번 블록, 합의는 2번 블록이고 각 6칸이다. 담긴 순서대로 앞칸부터 채운다.
   * 칸이 없는 역할(공람·수신처·시행자)과 6명을 넘는 인원은 본문에 자리가 없어 건너뛴다.
   */
  /**
   * 기안자 — 결재순서 1번은 언제나 로그인한 사람이다.
   * 결재경로 팝업에서 고르는 사람이 아니라 기안 자체로 정해지므로 여기서 만든다.
   */
  const drafterRow: GrwApprovalRow = useMemo(
    () => ({
      cd_dept: userInfo?.cd_dept,
      nm_dept: userInfo?.nm_dept ?? "",
      cd_emp: userInfo?.cd_emp,
      nm_emp: userInfo?.nm_emp ?? "",
      nm_role: "결재",
      fg_pos: userInfo?.fg_pos,
      nm_pos: userInfo?.nm_fg_pos,
    }),
    [userInfo]
  );

  /**
   * 본문 결재란에 들어갈 사람 — 기안자가 1번, 그다음이 결재경로에서 고른 사람들.
   * 기안자를 결재경로에서 또 고른 경우 중복되지 않게 걸러낸다.
   */
  const lineRows = useMemo(() => {
    const rest = approvalRows.filter(
      (r) =>
        !(
          BLOCK_OF_ROLE[r.nm_role] === BLOCK_OF_ROLE[drafterRow.nm_role] &&
          r.cd_emp &&
          r.cd_emp === drafterRow.cd_emp
        )
    );
    return [drafterRow, ...rest];
  }, [approvalRows, drafterRow]);

  /**
   * 결재란에 실제로 들어가는 사람 — 블록(결재→합의) 순서로 칸 번호까지 확정한다.
   * 본문 도장과 grw_approvel 행이 같은 목록에서 나오므로 서로 어긋나지 않는다.
   */
  const slots = useMemo(() => {
    const used: Record<number, number> = { 1: 0, 2: 0 };

    return Object.values(BLOCK_OF_ROLE)
      .sort((a, b) => a - b)
      .flatMap((block) =>
        lineRows.flatMap((r) => {
          if (BLOCK_OF_ROLE[r.nm_role] !== block) return [];
          const seq = used[block] + 1;
          // 6명을 넘는 인원은 본문에 자리가 없다
          if (seq > SLOTS_PER_BLOCK) return [];
          used[block] = seq;
          return [{ row: r, block, seq }];
        })
      );
  }, [lineRows]);

  const stamps = useMemo(() => {
    const map: Record<string, string> = {};

    slots.forEach(({ row, block, seq }) => {
      map[stampCellId(block, seq, "name")] = row.nm_emp ?? "";
      // 직급명이 오면 그걸 쓰고, 없으면 코드라도 보여준다
      map[stampCellId(block, seq, "role")] = row.nm_pos || row.fg_pos || "";
    });

    return map;
  }, [slots]);

  /**
   * grw_approvel에 넣을 행 — 결재·합의(본문 결재란 순서) 다음에 수신을 붙인다.
   *
   * 처음 넣을 때는 전부 TODO다. 이후 결재가 진행되면서 각자 DONE·REJECT 등으로 바뀐다.
   */
  const approvalLines = useMemo<GrwApprovalLineRow[]>(() => {
    const approvals: GrwApprovalLineRow[] = slots.map(({ row }, idx) => ({
      no_line: idx + 1,
      no_seq: idx + 1,
      fg_approve: FG_APPROVE[row.nm_role] ?? FG_APPROVE.결재,
      cd_emp: row.cd_emp ?? "",
      cd_dept: row.cd_dept ?? "",
      st_line: ST_LINE.TODO,
      yn_current: idx === 0 ? "Y" : "N",
      yn_required: "Y",
    }));

    const receivers: GrwApprovalLineRow[] = receiverRows.map((row, i) => ({
      no_line: approvals.length + i + 1,
      no_seq: approvals.length + i + 1,
      fg_approve: FG_APPROVE.수신처,
      cd_emp: row.cd_emp ?? "",
      cd_dept: row.cd_dept ?? "",
      st_line: ST_LINE.TODO,
      yn_current: "N",
      yn_required: "Y",
    }));

    return [...approvals, ...receivers];
  }, [slots, receiverRows]);

  /**
   * 서명칸 도장.
   *
   * 상신하면 기안자(결재 1번) 칸에 도장이 찍힌다. 뒷사람 도장은 각자 결재할 때 찍히므로
   * 여기서는 만들지 않는다.
   */
  const buildSignStamps = (st: string): Record<string, string> => {
    if (st !== ST_APPROVE.SUBMIT) return {};
    return {
      [stampCellId(BLOCK_OF_ROLE["결재"], 1, "sign")]: signStampHtml(
        drafterRow.nm_emp,
        new Date()
      ),
    };
  };

  const signStamps = useMemo(
    () => buildSignStamps(stApprove),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stApprove, drafterRow.nm_emp]
  );

  /**
   * 지금 팝업 내용을 저장용 한 덩어리로 모은다.
   *
   * st_approve는 인자로 받는다. setState는 다음 렌더에야 반영되므로, 상신처럼 상태를 바꾸면서
   * 곧바로 그 값으로 저장해야 하는 경우 state를 읽으면 직전 값(01)이 실려 나간다.
   */
  const collect = (st: string = stApprove): GrwDraftData => ({
    no_doc: noDoc,
    st_approve: st,
    no_form: form?.no_form ?? "",
    nm_form: nmForm,
    nm_title: title.trim(),
    nm_emp: nmEmp,
    dt_write: dayjs().format("YYYYMMDD"),
    // 편집기에서 직접 읽는다. body state는 blur 때만 갱신돼서, 편집기를 건드리지 않고
    // 결재라인만 추가한 뒤 저장하면 결재란이 빠진 낡은 값이 나간다.
    js_body: bodyRef.current?.getHtml() ?? body,
    approvalRows,
    approvalLines,
    receiverRows,
    files,
  });

  // 화면에서 내용을 읽어가거나 선택 결과를 밀어넣을 수 있게 열어둔다
  useImperativeHandle(ref, () => ({
    getSelectedValue: () => ({ draft: collect() }),
    setApprovalRows,
    setReceiverRows,
  }));

  /**
   * 결재라인추가 — 결재경로 팝업을 띄우고, 확인하면 담긴 목록을 표에 반영한다.
   * 화면에서 onApprovalLineAdd를 넘겼으면 그쪽에 맡긴다.
   */
  const openApprovalLinePopup = async () => {
    if (onApprovalLineAdd) {
      onApprovalLineAdd(collect());
      return;
    }

    const result = await showPopupModal({
      title: "결재경로",
      children: (
        <GrwApprovalLinePopup
          // 결재 1번은 언제나 기안자다 — 빼거나 순서를 바꿀 수 없다
          fixedLine={{
            cd_dept: drafterRow.cd_dept,
            nm_dept: drafterRow.nm_dept,
            cd_emp: drafterRow.cd_emp,
            nm_emp: drafterRow.nm_emp,
            fg_pos: drafterRow.fg_pos,
            nm_pos: drafterRow.nm_pos,
            tp_line: drafterRow.nm_role,
            no_order: 1,
          }}
          depts={depts}
          pathGroups={pathGroups}
          onLoadPathGroups={onLoadPathGroups}
          onAddPathGroup={onAddPathGroup}
          onDeletePathGroup={onDeletePathGroup}
          tabs={approvalLineTabs}
          onSearchEmp={onSearchEmp}
          // 지금 두 표에 있는 사람을 각자 탭 값으로 넘겨 이어서 편집하게 한다
          defaultValue={[
            ...approvalRows.map((r, i) => ({
              cd_dept: r.cd_dept,
              nm_dept: r.nm_dept,
              cd_emp: r.cd_emp,
              nm_emp: r.nm_emp,
              fg_pos: r.fg_pos,
              nm_pos: r.nm_pos,
              tp_line: r.nm_role || "결재",
              no_order: i + 1,
            })),
            ...receiverRows.map((r, i) => ({
              cd_dept: r.cd_dept,
              nm_dept: r.nm_dept,
              cd_emp: r.cd_emp,
              nm_emp: r.nm_emp,
              tp_line: RECEIVER_ROLE,
              no_order: i + 1,
            })),
          ]}
        />
      ),
      width: "900px",
      height: "760px",
    });

    // 취소/X는 undefined 또는 "close"
    if (!result || result === "close" || result === "ok") return;
    const picked = (result as any).approvalLines as GrwLineRow[] | undefined;
    if (!Array.isArray(picked)) return;

    // 수신처 탭에 담은 사람은 수신 표로, 나머지는 결재라인 표로 나눈다
    setApprovalRows(
      picked
        .filter((l) => l.tp_line !== RECEIVER_ROLE)
        .map((l) => ({
          cd_dept: l.cd_dept,
          nm_dept: l.nm_dept ?? "",
          cd_emp: l.cd_emp,
          nm_emp: l.nm_emp ?? "",
          // 어느 탭에 담겼는지를 역할로 쓴다 (결재/합의/공람/시행자)
          nm_role: l.tp_line ?? "",
          fg_pos: l.fg_pos,
          nm_pos: l.nm_pos,
        }))
    );

    setReceiverRows(
      picked
        .filter((l) => l.tp_line === RECEIVER_ROLE)
        .map((l) => ({
          cd_dept: l.cd_dept,
          nm_dept: l.nm_dept ?? "",
          cd_emp: l.cd_emp,
          nm_emp: l.nm_emp ?? "",
        }))
    );
  };

  const addFiles = (added: FileList | null) => {
    if (!added || added.length === 0) return;
    setFiles((prev) => [...prev, ...Array.from(added)]);
  };

  /** 빈 줄을 채워 최소 줄 수를 맞춘다 */
  const pad = <T,>(rows: T[], empty: T) =>
    rows.length >= MIN_ROWS
      ? rows
      : [...rows, ...Array.from({ length: MIN_ROWS - rows.length }, () => empty)];

  return (
    <div style={ROOT}>
      {/* 양식 · 제목 · 기안자 · 작성일 */}
      <div style={HEAD_GRID}>
        <span style={LABEL}>양식</span>
        <TextField value={nmForm} fullWidth sx={READONLY_SX} slotProps={RO} />
        <span style={LABEL}>제목</span>
        <TextField
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          sx={FIELD_SX}
        />

        <span style={LABEL}>기안자</span>
        <TextField value={nmEmp} fullWidth sx={READONLY_SX} slotProps={RO} />
        <span style={LABEL}>작성일</span>
        <TextField
          value={dayjs().format("YYYY-MM-DD")}
          fullWidth
          sx={READONLY_SX}
          slotProps={RO}
        />
      </div>

      {/* 본문(좌) + 결재/수신/첨부(우) */}
      <div style={BODY_ROW}>
        <div style={EDITOR_BOX}>
          <GrwDraftBody
            ref={bodyRef}
            body={body}
            setBody={setBody}
            stamps={stamps}
            signStamps={signStamps}
          />
        </div>

        <div style={SIDE}>
          <div style={{ display: "flex", gap: 6 }}>
            <Button
              type="button"
              variant="outlined"
              size="small"
              fullWidth
              disabled={submitting}
              // 임시저장은 지금 상태 그대로 — 기안작성으로 연 문서면 01
              onClick={() => onTempSave?.(collect())}
            >
              임시저장
            </Button>
            <Button
              type="button"
              variant="outlined"
              size="small"
              fullWidth
              disabled={submitting}
              onClick={async () => {
                if (submitting) return;
                setSubmitting(true);
                try {
                  // 상신하면 이 문서는 02가 된다. 이후 임시저장을 눌러도 02로 나간다.
                  setStApprove(ST_APPROVE.SUBMIT);
                  // 도장은 렌더를 기다리지 않고 지금 찍는다.
                  // collect()가 곧바로 본문을 읽어가므로 여기서 안 찍으면 저장값에 빠진다.
                  bodyRef.current?.applyStamps(
                    stamps,
                    buildSignStamps(ST_APPROVE.SUBMIT)
                  );

                  const saved = await onSubmit?.(collect(ST_APPROVE.SUBMIT));
                  // 저장에 성공했을 때만 닫는다. 실패하면 내용을 잃지 않게 열어 둔다.
                  if (saved === true) {
                    modalRef?.current?.handleConfirm(false);
                    return;
                  }
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {submitting ? "상신 중…" : "상신"}
            </Button>
            <Button
              type="button"
              variant="outlined"
              size="small"
              fullWidth
              // handleConfirm(false) → onClose(undefined) 로 그냥 닫힌다
              onClick={() => modalRef?.current?.handleConfirm(false)}
            >
              닫기
            </Button>
          </div>

          {/* 결재라인 — 그리드 화면과 같은 탭 제목 모양 (SnGrid의 title과 동일) */}
          <SnTab
            titles={["결재라인"]}
            width="100%"
            height="auto"
            header={[
              <Button
                type="button"
                variant="outlined"
                size="small"
                onClick={openApprovalLinePopup}
              >
                결재라인추가
              </Button>,
            ]}
            body={[
              <table style={TABLE}>
                <thead>
                  <tr>
                    <th style={{ ...TH, width: 36 }}>No</th>
                    <th style={TH}>부서명</th>
                    <th style={TH}>사원명</th>
                    <th style={{ ...TH, width: 60 }}>직급</th>
                    <th style={{ ...TH, width: 60 }}>역할</th>
                  </tr>
                </thead>
                <tbody>
                  {pad<GrwApprovalRow>(approvalRows, {
                    nm_dept: "",
                    nm_emp: "",
                    nm_role: "",
                  }).map((r, i) => (
                    <tr key={i}>
                      <td style={TD_C}>{r.nm_dept || r.nm_emp ? i + 1 : ""}</td>
                      <td style={TD}>{r.nm_dept}</td>
                      <td style={TD}>{r.nm_emp}</td>
                      {/* 결재란에 찍히는 값과 같은 규칙 — 명칭 없으면 코드 */}
                      <td style={TD}>{r.nm_pos || r.fg_pos || ""}</td>
                      <td style={TD}>{r.nm_role}</td>
                    </tr>
                  ))}
                </tbody>
              </table>,
            ]}
          />

          {/* 수신 */}
          <SnTab
            titles={["수신"]}
            width="100%"
            height="auto"
            body={[
              <table style={TABLE}>
                <thead>
                  <tr>
                    <th style={{ ...TH, width: 36 }}>No</th>
                    <th style={TH}>부서명</th>
                    <th style={TH}>사원명</th>
                  </tr>
                </thead>
                <tbody>
                  {pad<GrwReceiverRow>(receiverRows, {
                    nm_dept: "",
                    nm_emp: "",
                  }).map((r, i) => (
                    <tr key={i}>
                      <td style={TD_C}>{r.nm_dept || r.nm_emp ? i + 1 : ""}</td>
                      <td style={TD}>{r.nm_dept}</td>
                      <td style={TD}>{r.nm_emp}</td>
                    </tr>
                  ))}
                </tbody>
              </table>,
            ]}
          />

          {/* 첨부 — 남은 높이를 채우도록 flex 컨테이너로 감싼다 */}
          <div style={ATTACH_BOX}>
            <SnTab
              titles={["첨부"]}
              width="100%"
              height="100%"
              body={[
                <label
                  // 탭 body 안에서는 flex가 먹지 않아 높이를 직접 채운다
                  style={{
                    ...DROP,
                    height: "100%",
                    ...(isDragOver ? DROP_OVER : null),
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                  }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    addFiles(e.dataTransfer.files);
                  }}
                >
                  <input
                    type="file"
                    multiple
                    hidden
                    onChange={(e) => addFiles(e.target.files)}
                  />
                  {files.length === 0 ? (
                    <span style={{ color: "#c0392b" }}>Drag&amp;Drop</span>
                  ) : (
                    <div style={{ width: "100%", padding: "6px 8px" }}>
                      {files.map((f, i) => (
                        <div key={`${f.name}-${i}`} style={FILE_ROW}>
                          <span style={FILE_NAME}>{f.name}</span>
                          <span
                            style={FILE_DEL}
                            onClick={(e) => {
                              // label 안이라 그냥 두면 파일 선택창이 같이 열린다
                              e.preventDefault();
                              e.stopPropagation();
                              setFiles((prev) =>
                                prev.filter((_, idx) => idx !== i)
                              );
                            }}
                          >
                            ×
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </label>,
              ]}
            />
          </div>
        </div>
      </div>
    </div>
  );
});

//#region 스타일

const ROOT: React.CSSProperties = {
  position: "relative",
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  gap: 8,
};

const HEAD_GRID: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "60px minmax(140px, 1fr) 60px minmax(220px, 2fr)",
  alignItems: "center",
  gap: "8px 10px",
  flex: "0 0 auto",
};

const BODY_ROW: React.CSSProperties = {
  display: "flex",
  gap: 10,
  flex: 1,
  minHeight: 0,
};

const EDITOR_BOX: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  minHeight: 0,
  border: "1px solid rgb(186,191,199)",
  borderRadius: 4,
  overflow: "hidden",
};

const SIDE: React.CSSProperties = {
  width: 340,
  flex: "0 0 auto",
  display: "flex",
  flexDirection: "column",
  gap: 8,
  minHeight: 0,
  overflow: "auto",
};

const LABEL: React.CSSProperties = {
  fontSize: StyleSettings.font,
  whiteSpace: "nowrap",
};

const FIELD_SX = {
  "& .MuiInputBase-root": { height: StyleSettings.control_height },
  "& .MuiInputBase-input": { fontSize: StyleSettings.font },
};

/** 읽기 전용 칸은 회색으로 — 입력칸과 구분되게 */
const READONLY_SX = { ...FIELD_SX, backgroundColor: "#F1F1F1" };

const RO = { input: { readOnly: true } } as const;

const TABLE: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  tableLayout: "fixed",
};

const TH: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  padding: "4px 6px",
  border: "1px solid rgb(196 196 196)",
  background: "#fafbfc",
  whiteSpace: "nowrap",
};

const TD: React.CSSProperties = {
  fontSize: 12,
  padding: "4px 6px",
  border: "1px solid rgb(196 196 196)",
  height: 24,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const TD_C: React.CSSProperties = { ...TD, textAlign: "center" };

/** 첨부 탭은 우측 패널의 남은 높이를 채운다 (예전 DROP의 flex:1 역할) */
const ATTACH_BOX: React.CSSProperties = {
  flex: 1,
  minHeight: 150,
  display: "flex",
  flexDirection: "column",
};

const DROP: React.CSSProperties = {
  flex: 1,
  minHeight: 110,
  border: "1px solid rgb(0,0,255)",
  borderRadius: 4,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  fontSize: 13,
  overflow: "auto",
};

const DROP_OVER: React.CSSProperties = { background: "#eef4ff" };

const FILE_ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  fontSize: 12,
  padding: "2px 0",
};

const FILE_NAME: React.CSSProperties = {
  flex: 1,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const FILE_DEL: React.CSSProperties = {
  color: "#888",
  cursor: "pointer",
  padding: "0 4px",
};

const TAB_BTN = (active: boolean): React.CSSProperties => ({
  padding: "3px 14px",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  border: "1px solid rgb(186,191,199)",
  borderBottomColor: active ? "#fff" : "rgb(186,191,199)",
  borderTopLeftRadius: 4,
  borderTopRightRadius: 4,
  marginBottom: -1,
  background: active ? "#fff" : "#f2f4f7",
  color: active ? "#0b0c69" : "#555",
});

const TOOLBAR: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 2,
  padding: "4px 6px",
  borderTop: "1px solid rgb(186,191,199)",
  borderBottom: "1px solid rgb(186,191,199)",
  background: "#fafbfc",
  flex: "0 0 auto",
};

const TB_BTN: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 26,
  height: 24,
  padding: "0 6px",
  fontSize: 12,
  lineHeight: 1,
  cursor: "pointer",
  border: "1px solid #d0d5dd",
  borderRadius: 3,
  background: "#fff",
  color: "#333",
  position: "relative",
};

const TB_SELECT: React.CSSProperties = {
  height: 24,
  fontSize: 12,
  border: "1px solid #d0d5dd",
  borderRadius: 3,
  background: "#fff",
  color: "#333",
};

const SEP: React.CSSProperties = {
  display: "inline-block",
  width: 1,
  height: 18,
  margin: "0 3px",
  background: "#d0d5dd",
};

const COLOR_INPUT: React.CSSProperties = {
  width: 0,
  height: 0,
  padding: 0,
  border: "none",
  opacity: 0,
  position: "absolute",
};

const HTML_AREA: React.CSSProperties = {
  width: "100%",
  height: "100%",
  boxSizing: "border-box",
  padding: 8,
  border: "none",
  outline: "none",
  resize: "none",
  fontFamily: "monospace",
  fontSize: 12,
  lineHeight: 1.5,
  whiteSpace: "pre",
  overflow: "auto",
};

//#endregion

export default GrwDraftPopup;
